/** @jsxImportSource @emotion/react */
import React from "react";
import { css } from "@emotion/react";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { returnCheckboxAndRadioStyleInputStyle } from "./field-style-util";

type Props = {
  label?: string;
  description?: string;
  innerRef?: any;
} & React.ClassAttributes<HTMLInputElement> &
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;

const radioCircleStyle = css`
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5));
  background-color: ${colors.white};
`;

const wrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 0px;
  cursor: pointer;
  width: 100%;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08),
    0px 0px 0px 1px ${colors.greyBorder};
  background-color: ${colors.white};
  padding: 8px 12px 8px 12px;
  align-items: start !important;
  border-radius: 8px;
  &:has(> input:checked) {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.2),
      0px 0px 0px 1px ${colors.blueInteractive};
  }
  &:hover {
    &:has(> input:enabled:not(:checked)) {
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.12),
        0px 0px 0px 1px ${colors.greyBorder};
      > .customElement {
        background-color: ${colors.whiteHover};
      }
    }
  }
  ${returnCheckboxAndRadioStyleInputStyle("9999px")}
`;

const textWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

export const LabelledRadioField = ({
  label,
  value,
  innerRef,
  disabled,
  checked,
  description,
  ...rest
}: Props) => {
  return (
    <label css={wrapperStyle}>
      <input
        disabled={disabled}
        value={value}
        type="radio"
        ref={innerRef}
        checked={checked}
        {...rest}
      />
      <span className="customElement">
        <div className="checkedIndicator">
          <div css={radioCircleStyle} />
        </div>
      </span>
      <div css={textWrapperStyle}>
        {label && (
          <StyledText
            className="bodyTextSemibold mb-0"
            color={disabled ? colors.textSecondary : colors.textPrimary}
          >
            {label}
          </StyledText>
        )}
        {description && (
          <StyledText
            className="bodySubtextRegular mb-0"
            color={disabled ? colors.textSecondary : colors.textPrimary}
          >
            {description}
          </StyledText>
        )}
      </div>
    </label>
  );
};
